import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, ResponsiveContainer,
} from 'recharts';
import { DollarSign, TrendingUp, Receipt, BarChart3 } from 'lucide-react';
import type { CostTotals, TrendItem } from './costsService';

const CATEGORY_COLORS: Record<string, string> = {
  compute: '#3b82f6',
  storage: '#22c55e',
  database: '#a855f7',
  networking: '#f97316',
  other: '#6b7280',
  'third-party': '#eab308',
};

const PIE_COLORS = ['#3b82f6', '#22c55e', '#a855f7', '#f97316', '#6b7280', '#eab308', '#06b6d4', '#ec4899'];

function formatBRL(value: number) {
  return value.toLocaleString(getInterfaceLanguage(), { style: 'currency', currency: 'BRL' });
}

const RADIAN = Math.PI / 180;

function renderCustomLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: any) {
  if (percent < 0.03) return null;
  const radius = outerRadius + 24;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#475569" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11} fontWeight={600}>
      {name} {(percent * 100).toFixed(0)}%
    </text>
  );
}

function CustomBarTick({ x, y, payload }: any) {
  useLanguage();
  const label = payload.value || '';
  const truncated = label.length > 22 ? label.substring(0, 22) + '...' : label;
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fill="#64748b" fontSize={10}>
      {truncated}
    </text>
  );
}

interface CostsDashboardProps {
  totals: CostTotals | null;
  trend: TrendItem[];
  loading: boolean;
  selectedType: string;
}

export function CostsDashboard({ totals, trend, loading, selectedType }: CostsDashboardProps) {
  useLanguage();
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm animate-pulse">
            <div className="h-4 bg-slate-100 rounded w-24 mb-3" />
            <div className="h-8 bg-slate-100 rounded w-32" />
          </div>
        ))}
      </div>
    );
  }

  if (!totals) return null;

  const pieProviderData = (totals.byProvider || []).map((p) => ({
    name: p._id,
    value: p.totalBRL,
  }));

  const pieCategoryData = (totals.byCategory || []).map((c) => ({
    name: c._id,
    value: c.totalBRL,
  }));

  const barServiceData = (totals.byService || []).map((s) => ({
    name: s._id.length > 25 ? s._id.substring(0, 25) + '...' : s._id,
    BRL: s.totalBRL,
  }));

  const lineData = (trend || []).map((t) => ({
    date: t._id,
    custo: t.totalBRL,
    impostos: t.estimatedTaxBRL,
    total: t.totalWithTaxBRL,
  }));

  return (
    <div className="space-y-4">
      {/* TOTAL CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2">
            <DollarSign size={14} className="text-blue-500" />
            <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Total em BRL")}</span>
          </div>
          <p className="text-xl font-bold text-slate-900">{formatBRL(totals.totalBRL)}</p>
          <p className="text-[11px] text-slate-400 mt-1">{translateInterfaceText("USD")}{" "}{totals.totalUSD.toFixed(2)} {" "}{translateInterfaceText("convertido")}</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2">
            <Receipt size={14} className="text-amber-500" />
            <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Impostos")}</span>
          </div>
          <p className="text-xl font-bold text-slate-900">{formatBRL(totals.estimatedTaxBRL)}</p>
          <p className="text-[11px] text-slate-400 mt-1">{translateInterfaceText("Estimativa sobre o total")}</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingUp size={14} className="text-emerald-500" />
            <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Total c/ Impostos")}</span>
          </div>
          <p className="text-xl font-bold text-slate-900">{formatBRL(totals.totalWithTaxBRL)}</p>
          <p className="text-[11px] text-slate-400 mt-1">{translateInterfaceText("BRL + Taxas estimadas")}</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2">
            <BarChart3 size={14} className="text-indigo-500" />
            <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Câmbio")}</span>
          </div>
          <p className="text-xl font-bold text-slate-900">R$ {totals.rate?.toFixed(4) || '—'}</p>
          <p className="text-[11px] text-slate-400 mt-1">{translateInterfaceText("1 USD = BRL")}</p>
        </div>
      </div>

      {/* CHARTS ROW */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* PIE - Provider */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider block mb-3">{translateInterfaceText("Por Provedor")}</span>
          {pieProviderData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieProviderData}
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  innerRadius={35}
                  dataKey="value"
                  label={renderCustomLabel}
                  labelLine={false}
                >
                  {pieProviderData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatBRL(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-slate-400 text-center py-8">{translateInterfaceText("Sem dados")}</p>
          )}
        </div>

        {/* PIE - Category */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider block mb-3">{translateInterfaceText("Por Categoria")}</span>
          {pieCategoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieCategoryData}
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  innerRadius={35}
                  dataKey="value"
                  label={renderCustomLabel}
                  labelLine={false}
                >
                  {pieCategoryData.map((entry, i) => (
                    <Cell key={i} fill={CATEGORY_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatBRL(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-slate-400 text-center py-8">{translateInterfaceText("Sem dados")}</p>
          )}
        </div>

        {/* BAR - Services */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col">
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider block mb-3">{translateInterfaceText("Por Serviço")}</span>
          {barServiceData.length > 0 ? (
            <div className="flex-1">
              <ResponsiveContainer width="100%" height={Math.max(320, barServiceData.length * 36)}>
                <BarChart data={barServiceData} layout="vertical" margin={{ left: 0, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={<CustomBarTick />}
                    width={140}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(v: number) => formatBRL(v)}
                    contentStyle={{ fontSize: 11, borderRadius: 8 }}
                  />
                  <Bar dataKey="BRL" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-xs text-slate-400 text-center py-8">{translateInterfaceText("Sem dados")}</p>
          )}
        </div>

        {/* LINE - Trend */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col">
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider block mb-3">{translateInterfaceText("Tendência Diária")}</span>
          {lineData.length > 0 ? (
            <div className="flex-1">
              <ResponsiveContainer width="100%" height={Math.max(320, barServiceData.length * 36)}>
                <LineChart data={lineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v: number) => formatBRL(v)} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line type="monotone" dataKey="custo" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="impostos" stroke="#f97316" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="total" stroke="#22c55e" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-xs text-slate-400 text-center py-8">{translateInterfaceText("Sem dados")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default CostsDashboard;
